import type { CSSProperties } from "react";
import { repairInlineConfigJson } from "./metaobjectOperations";
import {
  DEFAULT_STYLING_CONFIG,
  PathwayKey,
  PathwayStyling,
  ScreenStyle,
  ScreenStyleKey,
  TStylingConfig,
  TypographyStyle,
  VisibilityConfig,
} from "@/types/stylingConfig";

let cachedConfig: TStylingConfig | null | undefined;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const deepMerge = <T extends Record<string, unknown>>(base: T, override?: Record<string, unknown> | null): T => {
  if (!override) return base;
  const result: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (value === undefined || value === null || value === "") continue;
    const baseValue = result[key];
    if (isObject(baseValue) && isObject(value)) {
      result[key] = deepMerge(baseValue as Record<string, unknown>, value);
    } else {
      result[key] = value;
    }
  }
  return result as T;
};

const parseConfigValue = (rawValue: string): TStylingConfig | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawValue);
  } catch {
    try {
      parsed = JSON.parse(repairInlineConfigJson(rawValue));
    } catch {
      return null;
    }
  }

  const config = Array.isArray(parsed) ? parsed[0] : parsed;
  if (!isObject(config)) return null;
  return config as TStylingConfig;
};

/**
 * Reads and caches `data-styling-config` from `#react-product`.
 * Merges merchant overrides onto production defaults so omitted fields keep current look.
 */
export const getStylingConfig = (): TStylingConfig => {
  if (cachedConfig !== undefined && cachedConfig !== null) {
    return cachedConfig;
  }

  try {
    if (typeof document === "undefined") {
      cachedConfig = DEFAULT_STYLING_CONFIG;
      return cachedConfig;
    }

    const hostElement = document.getElementById("react-product") as HTMLElement | null;
    const rawValue = hostElement?.dataset?.stylingConfig;

    if (!rawValue || !rawValue.trim()) {
      cachedConfig = DEFAULT_STYLING_CONFIG;
      return cachedConfig;
    }

    const parsed = parseConfigValue(rawValue);
    if (!parsed) {
      console.warn("[styling-config] Invalid data-styling-config; using defaults.");
      cachedConfig = DEFAULT_STYLING_CONFIG;
      return cachedConfig;
    }

    cachedConfig = deepMerge(
      DEFAULT_STYLING_CONFIG as unknown as Record<string, unknown>,
      parsed as unknown as Record<string, unknown>,
    ) as TStylingConfig;
    return cachedConfig;
  } catch (error) {
    console.error("[styling-config] Failed to parse data-styling-config:", error);
    cachedConfig = DEFAULT_STYLING_CONFIG;
    return cachedConfig;
  }
};

/** Test helper — clears the module cache. */
export const resetStylingConfigCache = (): void => {
  cachedConfig = undefined;
};

export const resolvePathwayKey = (options: {
  trackTitle?: string | null;
  trackKey?: string | null;
  pathname?: string | null;
}): PathwayKey => {
  const { trackTitle, trackKey, pathname } = options;
  if (trackKey === "BK" || trackTitle === "BATTLE_KART_TRACK") return "bk";
  if (pathname && (pathname.includes("battle-kart") || pathname.includes("battlekart"))) return "bk";
  return "hk";
};

export const getPathwayStyling = (pathway: PathwayKey): PathwayStyling => {
  const config = getStylingConfig();
  return config.pathways?.[pathway] ?? DEFAULT_STYLING_CONFIG.pathways![pathway]!;
};

export const getVisibilityConfig = (): VisibilityConfig => {
  return getStylingConfig().visibility ?? DEFAULT_STYLING_CONFIG.visibility!;
};

export const isAttractionVisible = (attraction: keyof VisibilityConfig): boolean => {
  const visibility = getVisibilityConfig();
  return visibility[attraction] !== false;
};

export const getScreenStyle = (pathway: PathwayKey, screen: ScreenStyleKey): ScreenStyle => {
  const pathwayStyle = getPathwayStyling(pathway);
  return {
    background: pathwayStyle.screens?.[screen]?.background ?? pathwayStyle.screenBackground,
    title: pathwayStyle.screens?.[screen]?.title,
    subtitle: pathwayStyle.screens?.[screen]?.subtitle,
  };
};

export const typographyToCss = (typo?: TypographyStyle, variablePrefix?: string): CSSProperties => {
  if (!typo) return {};

  const cssValue = (property: string, fallback: string | number | undefined): string | undefined => {
    if (fallback === undefined) return undefined;
    return variablePrefix ? `var(${variablePrefix}-${property}, ${fallback})` : String(fallback);
  };

  return {
    ...(typo.fontSize ? { fontSize: cssValue("font-size", typo.fontSize) } : {}),
    ...(typo.fontWeight !== undefined ? { fontWeight: cssValue("font-weight", typo.fontWeight) } : {}),
    ...(variablePrefix || typo.italic
      ? { fontStyle: cssValue("font-style", typo.italic ? "italic" : "normal") }
      : {}),
    ...(typo.color ? { color: cssValue("color", typo.color) } : {}),
  };
};

/**
 * Applies pathway CSS custom properties on `#react-product` (and documentElement
 * as a fallback for portaled UI). Call when pathway changes.
 */
export const applyPathwayCssVars = (pathway: PathwayKey): void => {
  if (typeof document === "undefined") return;

  const style = getPathwayStyling(pathway);
  const targets = [
    document.getElementById("react-product"),
    document.documentElement,
  ].filter(Boolean) as HTMLElement[];

  const vars: Record<string, string | undefined> = {
    "--booking-screen-bg": style.screenBackground,
    "--booking-ambient-dot-a": style.ambientDotA,
    "--booking-ambient-dot-b": style.ambientDotB,
    "--booking-progress-bg": style.progressBar?.background,
    "--booking-progress-radius": style.progressBar?.borderRadius,
    "--booking-progress-arrow-radius": style.progressBar?.arrowRadius,
    "--booking-progress-label-size": style.progressBar?.labelFontSize,
    "--booking-progress-label-color": style.progressBar?.labelColor,
    "--booking-cta-bg": style.cta?.background,
    "--booking-cta-radius": style.cta?.borderRadius,
    "--booking-cta-font-size": style.cta?.fontSize,
    "--booking-cta-padding-x": style.cta?.paddingX,
    "--booking-cta-padding-y": style.cta?.paddingY,
    "--booking-cta-color": style.cta?.color,
    "--booking-prev-bg": style.previousButton?.background,
    "--booking-prev-color": style.previousButton?.iconColor,
    "--booking-prev-radius": style.previousButton?.borderRadius,
    "--booking-sticky-bg": style.stickySummary?.background,
    "--booking-sticky-desktop-accent": style.stickySummary?.desktopAccent,
    "--booking-sticky-radius": style.stickySummary?.borderRadius,
    "--booking-pill-bg": style.stickySummary?.pillBackground,
    "--booking-pill-color": style.stickySummary?.pillTextColor,
    "--booking-pill-font-size": style.stickySummary?.pillFontSize,
    "--booking-pill-radius": style.stickySummary?.pillBorderRadius,
    "--booking-modal-border-bg": style.modals?.borderBackground,
    "--booking-modal-content-bg": style.modals?.contentBackground,
    "--booking-modal-radius": style.modals?.borderRadius,
    "--booking-modal-title-color": style.modals?.titleColor,
    "--booking-modal-body-color": style.modals?.bodyColor,
    "--booking-modal-btn-bg": style.modals?.buttonBackground,
    "--booking-modal-btn-radius": style.modals?.buttonBorderRadius,
    "--booking-modal-btn-font-size": style.modals?.buttonFontSize,
  };

  for (const el of targets) {
    for (const [name, value] of Object.entries(vars)) {
      if (value) el.style.setProperty(name, value);
    }
  }
};
